import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, isTemporaryProviderError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { ArrowLeft, CheckCircle2, Clock, Copy, ExternalLink, KeyRound, Play, RefreshCw, UserPlus, Wallet } from "lucide-react";
import { toast } from "sonner";
import GameActionsBar from "../components/GameActionsBar";
import { siteConfig } from "../lib/siteConfig";
import { useTheme } from "../context/ThemeContext";

export default function GamePlay() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const [game, setGame] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [requests, setRequests] = useState([]);
  const [automationAvailability, setAutomationAvailability] = useState(null);
  const [providerBalance, setProviderBalance] = useState(null);
  const [providerSafeBalance, setProviderSafeBalance] = useState(null);
  const [providerBalanceLoaded, setProviderBalanceLoaded] = useState(false);
  const balanceRefreshInFlight = useRef(false);

  useEffect(() => {
    api.get(`/games/${id}`).then(({ data }) => setGame(data)).catch(() => nav("/games"));
  }, [id, nav]);

  const loadRequests = useCallback(async () => {
    if (!user || user === false) return;
    const { data } = await api.get("/me/requests");
    setRequests(Array.isArray(data) ? data : []);
  }, [user]);

  useEffect(() => {
    loadRequests().catch(() => {});
  }, [loadRequests]);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/automation/available-games")
      .then(({ data }) => setAutomationAvailability(data))
      .catch(() => setAutomationAvailability({ attachedGameIds: [], supportedGameIds: [] }));
  }, [user]);

  const gameAutomationId = useMemo(() => automationGameId(game?.name || id), [game?.name, id]);
  const attachedAutomationIds = useMemo(
    () => new Set(automationAvailability?.attachedGameIds || []),
    [automationAvailability],
  );
  const supportedAutomationIds = useMemo(
    () => new Set(automationAvailability?.supportedGameIds || []),
    [automationAvailability],
  );
  const automationLoaded = automationAvailability !== null;
  const automationSupported = supportedAutomationIds.has(gameAutomationId);
  const automationConnected = attachedAutomationIds.has(gameAutomationId);

  const gameRequests = useMemo(
    () => {
      const autoId = automationGameId(game?.name || id);
      return requests.filter((req) => req.game_id === id || req.game_id === autoId);
    },
    [requests, id, game?.name],
  );
  const accountRequest = useMemo(() => latestRequest(gameRequests, "create_account"), [gameRequests]);
  const resetRequest = useMemo(() => latestRequest(gameRequests, "reset_password"), [gameRequests]);
  const createPending = accountRequest?.status === "pending";
  const createApproved = accountRequest?.status === "approved";
  const automationReadyForPlayer = automationConnected || createApproved;
  const showManualAccountPending = createPending && !automationReadyForPlayer;
  const visibleAccountRequest = automationReadyForPlayer && createPending ? null : accountRequest;
  const pageClass = isDark ? "min-h-screen bg-[#05070D] text-white" : "min-h-screen bg-slate-50 text-slate-950";
  const mutedTextClass = isDark ? "text-slate-300" : "text-slate-600";
  const softTextClass = isDark ? "text-slate-400" : "text-slate-500";
  const strongTextClass = isDark ? "text-white" : "text-slate-950";
  const panelClass = isDark
    ? "mt-10 md:mt-12 rounded-3xl border border-white/10 bg-[#081421] p-5 shadow-sm md:p-8"
    : "mt-10 md:mt-12 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-8";

  const fetchProviderBalance = useCallback(async () => {
    if (!createApproved) return null;
    const { data } = await api.post("/automation/balance", { gameName: gameAutomationId }, { skipMaintenance: true });
    const result = normalizeBalanceResponse(data);
    if (result.amount !== null) setProviderBalance(result.amount);
    else if (!result.soft) setProviderBalance(null);
    if (result.safeAmount !== null) setProviderSafeBalance(result.safeAmount);
    else if (!result.soft) setProviderSafeBalance(null);
    setProviderBalanceLoaded(true);
    return result;
  }, [createApproved, gameAutomationId]);

  useEffect(() => {
    if (!user || user === false || !createApproved) {
      setProviderBalance(null);
      setProviderSafeBalance(null);
      setProviderBalanceLoaded(false);
      return;
    }
    setProviderBalance(null);
    setProviderSafeBalance(null);
    setProviderBalanceLoaded(false);
    fetchProviderBalance().catch((error) => {
      if (isBalanceRefreshError(error)) {
        setProviderBalanceLoaded(true);
        return;
      }
      setProviderBalance(null);
      setProviderSafeBalance(null);
      setProviderBalanceLoaded(false);
    });
  }, [user, createApproved, gameAutomationId, fetchProviderBalance]);

  useEffect(() => {
    if (!user || user === false || !createApproved) return undefined;

    const refreshLiveBalance = async () => {
      if (document.hidden || balanceRefreshInFlight.current) return;
      balanceRefreshInFlight.current = true;
      try {
        await fetchProviderBalance();
      } catch (error) {
        if (isBalanceRefreshError(error)) {
          setProviderBalanceLoaded(true);
        }
      } finally {
        balanceRefreshInFlight.current = false;
      }
    };

    const onVisibilityChange = () => {
      if (!document.hidden) refreshLiveBalance();
    };

    const timer = window.setInterval(refreshLiveBalance, 20000);
    window.addEventListener("focus", refreshLiveBalance);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshLiveBalance);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user, createApproved, gameAutomationId, fetchProviderBalance]);

  const onRefreshBalance = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      if (!createApproved) {
        toast.info("Create or approve this game account before refreshing balance.");
        return;
      }
      const result = await fetchProviderBalance();
      if (result?.soft) {
        toast.info(result.warning);
      } else {
        toast.success("Game balance refreshed");
      }
    } catch (e) {
      showGamePlayError(e);
    } finally {
      setTimeout(() => setRefreshing(false), 400);
    }
  };

  const onCreateAccount = async () => {
    if (creating) return;
    if (createApproved) {
      toast.info("Account details are shown below");
      return;
    }
    if (!automationConnected) {
      toast.info("Game automation is reconnecting. Please try Create account again shortly.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/automation/create-account", {
        gameName: gameAutomationId,
        username: user.username || user.email,
      });
      const account = data?.account || {};
      if (account.username || account.password) {
        const accountUsername = account.username || account.account || account.provider_requested_username || account.provider_returned_username || account.provider_user_id || account.user_id || "";
        const optimisticRequest = {
          id: `created-${gameAutomationId}`,
          type: "create_account",
          status: "approved",
          game_id: gameAutomationId,
          created_at: new Date().toISOString(),
          admin_note: `Username: ${accountUsername}\nPassword: ${account.password || ""}`,
        };
        setRequests((current) => [
          optimisticRequest,
          ...current.filter((req) => !(req.type === "create_account" && (req.game_id === id || req.game_id === gameAutomationId))),
        ]);
      }
      setProviderBalance(0);
      setProviderSafeBalance(null);
      setProviderBalanceLoaded(true);
      toast.success("Game account created and saved");
      loadRequests().catch(() => {});
      fetchProviderBalance().catch(() => {});
    } catch (e) {
      showGamePlayError(e);
    } finally {
      setCreating(false);
    }
  };

  if (!user) return (
    <div className={pageClass}>
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold mb-4">Sign in to play</h1>
        <p className={`${mutedTextClass} mb-8`}>You need a {siteConfig.name} account to access this game.</p>
        <Link to="/login" data-testid="play-login-cta" className="px-8 py-3 bg-[#2F6FA5] text-white font-bold rounded-full inline-block">Sign in</Link>
      </div>
    </div>
  );

  if (!game) return <div className={pageClass}><Navbar /></div>;

  const mainBalanceNumber = parseBalanceNumber(providerBalance);
  const safeBalanceNumber = parseBalanceNumber(providerSafeBalance);
  const displayBalance = providerBalanceLoaded
    ? safeBalanceNumber !== null && safeBalanceNumber > 0
      ? (mainBalanceNumber || 0) + safeBalanceNumber
      : providerBalance
    : null;

  return (
    <div className={pageClass}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-10 pb-20">
        <button onClick={() => nav(-1)} className={`text-sm flex items-center gap-2 mb-6 ${isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-950"}`} data-testid="play-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className={`relative mx-auto w-full max-w-[19rem] overflow-hidden rounded-2xl border aspect-[5/4] shadow-sm md:max-w-none md:aspect-square ${
            isDark ? "border-white/10 bg-[#081421]" : "border-slate-200 bg-white"
          }`}>
            {game.image_url ? (
              <img src={game.image_url} alt={game.name} className="h-full w-full object-contain p-3 md:object-cover md:p-0" />
            ) : (
              <div className={`w-full h-full ${isDark ? "bg-[#050B14]" : "bg-slate-100"}`} />
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#2F6FA5] mb-3">{game.category}</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">{game.name}</h1>
            <p className={`mt-4 ${mutedTextClass}`}>{game.description}</p>
            <div className={`mt-6 flex gap-6 text-sm ${softTextClass}`}>
              <span>RTP <strong className={strongTextClass}>{game.rtp}%</strong></span>
              <span>Min <strong className={strongTextClass}>{game.min_bet}</strong></span>
              <span>Max <strong className={strongTextClass}>{game.max_bet}</strong></span>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {game.play_url ? (
                <a
                  href={game.play_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="play-now-cta"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#2F6FA5] hover:bg-[#2F6FA5] text-white font-bold text-sm tracking-wide transition-all shadow-[0_0_24px_rgba(47,111,165,0.35)]"
                >
                  <Play className="w-4 h-4 fill-current" />
                  Play now
                  <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                </a>
              ) : (
                <span className={`inline-flex items-center gap-2 px-7 py-3 rounded-full border text-sm font-medium ${
                  isDark ? "border-white/10 bg-white/5 text-slate-300" : "border-slate-200 bg-slate-100 text-slate-500"
                }`} data-testid="play-now-unavailable">
                  <Play className="w-4 h-4" />
                  Play link unavailable
                </span>
              )}

              <div className={`inline-flex items-center gap-3 px-5 py-3 rounded-full border border-[#2F6FA5]/25 shadow-sm ${
                isDark ? "bg-[#081421]" : "bg-white"
              }`} data-testid="play-balance">
                <Wallet className="w-5 h-5 text-[#2F6FA5]" />
                <span className={`${softTextClass} text-sm`}>Game balance</span>
                <span className="font-display font-bold tabular-nums text-lg">
                  {formatBalanceValue(displayBalance)}
                </span>
                {providerBalanceLoaded && safeBalanceNumber !== null && safeBalanceNumber > 0 && (
                  <span className={`basis-full pl-8 text-xs ${softTextClass}`}>
                    Main {formatBalanceValue(mainBalanceNumber || 0)} + Safe {formatBalanceValue(safeBalanceNumber)}
                  </span>
                )}
              </div>

              <button
                onClick={onRefreshBalance}
                disabled={refreshing}
                data-testid="refresh-balance"
                title="Refresh balance"
                className={`inline-flex items-center gap-2 px-4 py-3 rounded-full border text-sm font-medium shadow-sm transition-all disabled:opacity-50 ${
                  isDark ? "border-white/10 bg-[#081421] hover:border-[#2F6FA5]/50 hover:bg-[#0B2034]" : "border-slate-200 bg-white hover:border-[#2F6FA5]/40 hover:bg-[#EAF4FC]"
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                <span>Refresh balance</span>
              </button>

              <button
                onClick={onCreateAccount}
                disabled={creating || !automationLoaded}
                data-testid="create-game-account"
                title={!automationLoaded ? "Checking backend connection" : createApproved ? "Account details" : !automationReadyForPlayer ? "Send manual account request" : "Create game account"}
                className={`inline-flex items-center gap-2 px-4 py-3 rounded-full border text-sm font-medium transition-all disabled:opacity-50 ${
                  automationReadyForPlayer
                    ? "border-emerald-500/35 bg-emerald-50 text-emerald-700 hover:border-emerald-500/70 hover:bg-emerald-100"
                    : "border-[#2F6FA5]/35 bg-[#EAF4FC] text-[#2F6FA5] hover:border-[#2F6FA5]/70 hover:bg-[#dbeefa]"
                }`}
              >
                <UserPlus className="w-4 h-4" />
                <span>{creating ? "Submitting..." : createApproved ? "Account details" : !automationLoaded ? "Checking..." : !automationReadyForPlayer ? showManualAccountPending ? "Request pending" : "Request account" : "Create account"}</span>
              </button>
            </div>
            {automationLoaded && !automationConnected && !createApproved && (
              <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                {automationSupported
                  ? "Automatic backend is reconnecting. Please try again shortly."
                  : "Automatic account creation is temporarily unavailable for this game. Please contact support."}
              </div>
            )}
          </div>
        </div>

        {/* Account actions */}
        <div className={panelClass}>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#2F6FA5] mb-1">Account actions</div>
              <h3 className="font-display text-xl font-bold">Manage your play</h3>
            </div>
          </div>
          {(visibleAccountRequest || resetRequest) && (
            <RequestDetailsPanel
              accountPendingText={
                automationReadyForPlayer
                  ? "Automatic creation is available. Tap Create now above to finish this game account."
                  : "Your game account request is pending admin approval. Login details will show here after approval."
              }
              accountRequest={visibleAccountRequest}
              resetRequest={resetRequest}
            />
          )}
          <GameActionsBar
            gameId={id}
            game={game}
            automationConnected={automationReadyForPlayer}
            providerBalance={providerBalance}
            providerBalanceLoaded={providerBalanceLoaded}
            onLoadComplete={async () => {
              await refreshUser();
              await fetchProviderBalance().catch(() => {});
            }}
            onRequestCreated={(req) => {
              setRequests((current) => [req, ...current]);
              loadRequests().catch(() => {});
            }}
          />
        </div>
      </div>
    </div>
  );
}

function latestRequest(rows, type) {
  const typed = rows.filter((req) => req.type === type && !isArchivedRequestStatus(req.status));
  return typed.find((req) => req.status === "approved")
    || typed.find((req) => req.status === "pending")
    || typed[0]
    || null;
}

function isArchivedRequestStatus(status = "") {
  return ["cancelled", "canceled", "superseded", "expired"].includes(String(status).toLowerCase());
}

function showGamePlayError(error) {
  if (isBalanceRefreshError(error)) {
    toast.info("Live game balance is refreshing. Please try again in a moment.");
    return;
  }
  const message = formatApiError(error, { audience: "player" });
  if (isTemporaryProviderError(error)) {
    toast.warning(message);
    return;
  }
  toast.error(message);
}

function normalizeBalanceResponse(payload) {
  const body = balancePayload(payload);
  const amount = extractBalanceAmount(payload);
  const safeAmount = extractSafeBalanceAmount(payload);
  const fallbackWarning = "Live game balance is refreshing. Try again in a moment.";
  const warning = isBalanceRefreshError(body?.warning)
    ? fallbackWarning
    : body?.warning || fallbackWarning;
  return {
    amount,
    safeAmount,
    soft: Boolean(body?.temporarily_unavailable || body?.last_known || body?.cached || body?.warning),
    warning,
  };
}

function balancePayload(payload) {
  if (payload?.balance && typeof payload.balance === "object") return payload.balance;
  return payload || {};
}

function isBalanceRefreshError(error) {
  const text = apiErrorText(error);
  return [
    "game connection is being refreshed",
    "live balance is refreshing",
    "live sync retrying",
    "could not verify current game balance",
    "could not read the current game balance",
    "could not read current game balance",
    "provider balance",
    "provider is rate-limiting live checks",
    "rate-limiting live checks",
    "rate limit",
    "connection is being refreshed",
    "temporarily_unavailable",
  ].some((part) => text.includes(part));
}

function apiErrorText(error) {
  return [
    error?.response?.data,
    error?.response?.data?.detail,
    error?.message,
  ].map((value) => (typeof value === "string" ? value : value ? JSON.stringify(value) : "")).join(" ").toLowerCase();
}

function extractBalanceAmount(payload) {
  const candidates = [
    payload?.balance?.balance,
    payload?.balance?.user_balance,
    payload?.balance?.userBalance,
    payload?.balance?.score,
    payload?.balance?.data?.user_balance,
    payload?.balance?.data?.balance,
    payload?.balance?.response?.data?.user_balance,
    payload?.balance?.response?.data?.balance,
    payload?.response?.data?.user_balance,
    payload?.response?.data?.balance,
    payload?.data?.user_balance,
    payload?.data?.balance,
    payload?.user_balance,
    payload?.userBalance,
    payload?.score,
    payload?.balance,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    if (typeof value === "number" || typeof value === "string") return value;
  }
  return null;
}

function extractSafeBalanceAmount(payload) {
  const candidates = [
    payload?.balance?.safe_balance,
    payload?.balance?.safeBalance,
    payload?.balance?.safe_balance_amount,
    payload?.balance?.safeBalanceAmount,
    payload?.balance?.safe_score,
    payload?.balance?.safeScore,
    payload?.balance?.soltmainaScore,
    payload?.balance?.slotmainaScore,
    payload?.balance?.raw?.safe_balance,
    payload?.balance?.raw?.safeBalance,
    payload?.balance?.raw?.soltmainaScore,
    payload?.balance?.raw?.slotmainaScore,
    payload?.balance?.response?.data?.safe_balance,
    payload?.balance?.response?.data?.safeBalance,
    payload?.balance?.response?.data?.soltmainaScore,
    payload?.balance?.response?.data?.slotmainaScore,
    payload?.safe_balance,
    payload?.safeBalance,
    payload?.safe_score,
    payload?.safeScore,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    if (typeof value === "number" || typeof value === "string") return value;
  }
  return null;
}

function parseBalanceNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(String(value).replace(/[$,]/g, "").trim());
  return Number.isFinite(numeric) ? numeric : null;
}

function formatBalanceValue(value) {
  if (value === null || value === undefined || value === "") return "--";
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return `$${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return String(value);
}

function RequestDetailsPanel({ accountPendingText, accountRequest, resetRequest }) {
  const hideResetAfterApprovedAccount = accountRequest?.status === "approved" && resetRequest?.status === "approved";
  return (
    <div className="mb-5 grid md:grid-cols-2 gap-3">
      <RequestCard
        icon={UserPlus}
        title="Game account"
        request={accountRequest}
        pendingText={accountPendingText}
        approvedTitle="Account details"
        emptyText="Username/password not added yet. Admin can edit this request from Requests."
      />
      {!hideResetAfterApprovedAccount && (
        <RequestCard
          icon={KeyRound}
          title="Password reset"
          request={resetRequest}
          pendingText="Password reset is processing."
          approvedTitle="New password"
          emptyText="New password not added yet. Admin can edit this request from Requests."
        />
      )}
    </div>
  );
}

function RequestCard({ icon: Icon, title, request, pendingText, approvedTitle, emptyText }) {
  const { isDark } = useTheme();
  if (!request) return null;
  const status = request.status || "pending";
  const approved = status === "approved";
  const rejected = status === "rejected";
  const detail = (request.admin_note || "").trim();
  const parsed = parseDetails(detail);
  const displayUsername = parsed.username || parsed.accountNumber || "";
  const loginDetails = [
    `Username: ${displayUsername}`,
    `Password: ${parsed.password || ""}`,
  ].filter((line) => !line.endsWith(": ")).join("\n");

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${
      isDark ? "border-white/10 bg-[#050B14]" : "border-slate-200 bg-white"
    }`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#EAF4FC] border border-[#2F6FA5]/20 grid place-items-center shrink-0">
            <Icon className="w-4 h-4 text-[#2F6FA5]" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-sm truncate">{title}</div>
            <div className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>{formatRequestDate(request.created_at)}</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border capitalize ${statusTone(status)}`}>
          {approved ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {status.replace("_", " ")}
        </span>
      </div>

      {approved ? (
        detail || request.type === "create_account" || request.type === "reset_password" ? (
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-2">{approvedTitle}</div>
            <div className="rounded-xl border border-[#2F6FA5]/20 bg-[#EAF4FC] p-3">
              {request.type === "create_account" ? (
                <div className="space-y-2">
                  <CredentialLine label="Username" value={displayUsername} />
                  <CredentialLine label="Password" value={parsed.password || ""} />
                  <button
                    type="button"
                    onClick={() => copyDetail(loginDetails)}
                    disabled={!parsed.username && !parsed.password}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-[#2F6FA5] hover:bg-slate-50 disabled:opacity-40"
                    title="Copy username/pass"
                  >
                    <Copy className="h-4 w-4" />
                    Copy username/pass
                  </button>
                </div>
              ) : request.type === "reset_password" ? (
                <CredentialLine label="Password" value={parsed.password || detail} />
              ) : (
                <pre className="whitespace-pre-wrap break-words text-sm text-slate-700 font-sans leading-relaxed">{detail}</pre>
              )}
              {request.type !== "create_account" && (
                <button
                  type="button"
                  onClick={() => copyDetail(detail)}
                  disabled={!detail}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[#2F6FA5] text-xs hover:bg-slate-50 disabled:opacity-40"
                  title="Copy"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-500"}`}>{emptyText}</p>
        )
      ) : rejected ? (
        <p className="text-sm text-red-600">{detail || "Request rejected. Contact support for help."}</p>
      ) : (
        <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-500"}`}>{pendingText}</p>
      )}
    </div>
  );
}

function CredentialLine({ label, value }) {
  return (
    <div className="grid gap-1 text-sm sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-3 sm:items-start">
      <span className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</span>
      <div className="flex min-w-0 items-center gap-2">
        <span className={`font-semibold break-words ${value ? "text-slate-950" : "text-slate-400 italic"}`}>
          {value || "Not added yet"}
        </span>
        {value && (
          <button
            type="button"
            onClick={() => copyDetail(value)}
            className="shrink-0 rounded-md border border-slate-200 bg-white p-1 text-[#2F6FA5] hover:bg-slate-50"
            title={`Copy ${label}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function gameDisplayName(value = "") {
  const key = automationGameId(value);
  const names = {
    bluedragon: "Blue Dragon",
    riversweeps: "RiverSweeps",
    lasvegassweeps: "Vegas Sweeps",
    firekirin: "FireKirin",
    gamevault: "Game Vault",
    gamevault2: "Game Vault",
    pandamaster: "Panda Master",
    orionstars: "Orion Stars",
    cashfrenzy: "Cash Frenzy",
    mrallinone: "Mr All In One",
    cashmachine: "Cash Machine",
    vblink: "Vblink",
    gameroom: "Game Room",
    ultrapanda: "Ultra Panda",
    noble777: "Noble 777",
    vegasroll: "Vegas Roll",
  };
  return names[key] || String(value || "Game").replace(/[-_]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function parseDetails(text = "") {
  const out = {};
  text.split(/\r?\n/).forEach((line) => {
    const [rawKey, ...rest] = line.split(":");
    const key = rawKey.trim().toLowerCase();
    const value = rest.join(":").trim();
    if (!value) return;
    if (["account number", "account no", "account #", "account id", "player id", "user id", "userid", "provider user id"].includes(key)) out.accountNumber = value;
    if (["username", "user", "login"].includes(key)) out.username = value;
    if (["password", "pass", "new password"].includes(key)) out.password = value;
    if (["notes", "note"].includes(key)) out.notes = value;
  });
  return out;
}

function statusTone(status) {
  if (status === "approved") return "bg-emerald-50 border-emerald-200 text-emerald-700";
  if (status === "rejected") return "bg-red-50 border-red-200 text-red-700";
  return "bg-slate-100 border-slate-200 text-slate-600";
}

function formatRequestDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

async function copyDetail(value) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success("Copied");
  } catch {
    toast.error("Could not copy");
  }
}

function automationGameId(value = "") {
  const key = String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
  const map = {
    juwa: "juwa",
    juwa20: "juwa2",
    juwa2: "juwa2",
    riversweeps: "riversweeps",
    vegassweeps: "lasvegassweeps",
    lasvegassweeps: "lasvegassweeps",
    firekirin: "firekirin",
    gamevault: "gamevault",
    gamevault20: "gamevault2",
    gamevault2: "gamevault2",
    pandamaster: "pandamaster",
    orionstars: "orionstars",
    mafia: "mafia",
    cashfrenzy: "cashfrenzy",
    mrallinone: "mrallinone",
    yolo: "yolo",
    cashmachine: "cashmachine",
    vblink: "vblink",
    milkyways: "milkyway",
    milkyway: "milkyway",
    luckystrike: "luckystrike",
    luckystrikes: "luckystrike",
    gameroom: "gameroom",
    ultrapanda: "ultrapanda",
    bluedragon: "bluedragon",
    noble777: "noble777",
    vegasroll: "vegasroll",
    winstar: "winstar",
  };
  return map[key] || key;
}
